// ── Chart maths ─────────────────────────────────────────────────────────────
// Pure geometry and downsampling helpers shared by the SVG chart primitives in
// `src/components/charts/`. Nothing here touches the DOM, so it is all unit-tested.

/** One sample. `t` is epoch milliseconds; `v` is null where the source reported nothing. */
export interface ChartPoint {
  t: number;
  v: number | null;
}

/** Linear map from a data domain onto a pixel (viewBox) range. */
export const linearScale = (
  value: number,
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number,
): number => {
  const span = domainMax - domainMin;
  if (span === 0) return (rangeMin + rangeMax) / 2;
  return rangeMin + ((value - domainMin) / span) * (rangeMax - rangeMin);
};

/** Inverse of `linearScale` along x, clamped to the domain. */
export const inverseScale = (
  position: number,
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number,
): number => {
  const span = rangeMax - rangeMin;
  if (span === 0) return domainMin;
  const ratio = (position - rangeMin) / span;
  const value = domainMin + ratio * (domainMax - domainMin);
  return Math.min(Math.max(value, domainMin), domainMax);
};

/**
 * Axis ticks on round numbers.
 *
 * Picks a 1/2/5 x 10^n step so labels read as 0, 25, 50 rather than the 0, 2, 4, 6, 8 that
 * naive `max/count` division produces for a max of 9.
 */
export const niceTicks = (
  min: number,
  max: number,
  count = 5,
): { ticks: number[]; niceMin: number; niceMax: number } => {
  if (!isFinite(min) || !isFinite(max) || count < 2) {
    return { ticks: [0], niceMin: 0, niceMax: 1 };
  }

  if (min === max) {
    // A flat series still deserves a readable axis around its single value.
    const pad = Math.abs(min) > 0 ? Math.abs(min) * 0.1 : 1;
    min -= pad;
    max += pad;
  }

  const rawStep = (max - min) / (count - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalised = rawStep / magnitude;
  const step =
    magnitude * (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10);

  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks: number[] = [];
  // Half a step of slack absorbs the float drift that would otherwise drop the final tick.
  for (let v = niceMin; v <= niceMax + step / 2; v += step) {
    // Re-round each tick: repeated addition of e.g. 0.1 accumulates visible error.
    ticks.push(Number(v.toFixed(10)));
  }

  return { ticks, niceMin, niceMax };
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Candidate spacings for time ticks, coarsest last. */
const TIME_STEPS = [
  MINUTE,
  5 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  HOUR,
  3 * HOUR,
  6 * HOUR,
  12 * HOUR,
  DAY,
  7 * DAY,
  30 * DAY,
  90 * DAY,
  365 * DAY,
];

/**
 * Time-axis ticks snapped to round boundaries (the hour, the day) rather than to the start of
 * the range, so a 7-day chart labels midnights instead of arbitrary offsets.
 */
export const timeTicks = (startMs: number, endMs: number, maxTicks = 8): number[] => {
  if (!isFinite(startMs) || !isFinite(endMs) || endMs <= startMs || maxTicks < 2) {
    return [startMs];
  }

  const span = endMs - startMs;
  const step =
    TIME_STEPS.find((candidate) => span / candidate <= maxTicks - 1) ??
    TIME_STEPS[TIME_STEPS.length - 1];

  const ticks: number[] = [];
  // Snapping in UTC keeps ticks stable across a DST change; the label formatter, not the tick
  // position, is what renders them in the viewer's zone.
  let cursor = Math.ceil(startMs / step) * step;
  while (cursor <= endMs && ticks.length <= maxTicks + 2) {
    ticks.push(cursor);
    cursor += step;
  }

  return ticks.length > 0 ? ticks : [startMs, endMs];
};

/** Median gap between consecutive samples. Used to decide where a series has a real gap. */
export const medianInterval = (points: ChartPoint[]): number => {
  if (points.length < 2) return 0;
  const gaps: number[] = [];
  for (let i = 1; i < points.length; i += 1) {
    gaps.push(points[i].t - points[i - 1].t);
  }
  gaps.sort((a, b) => a - b);
  return gaps[Math.floor(gaps.length / 2)];
};

/**
 * Split a series into contiguous runs of non-null samples.
 *
 * A run ends at a null value or at a time gap wider than `gapFactor` times the median interval.
 * This is what makes an outage render as a break in the line instead of a straight line drawn
 * across it — the standalone StarChart site drew that straight line, so a six-hour outage looked
 * like a smooth decline.
 */
export const splitOnGaps = (
  points: ChartPoint[],
  gapFactor = 3,
): { t: number; v: number }[][] => {
  if (points.length === 0) return [];

  const median = medianInterval(points);
  const maxGap = median > 0 ? median * gapFactor : Infinity;

  const runs: { t: number; v: number }[][] = [];
  let current: { t: number; v: number }[] = [];

  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];

    if (point.v === null || !isFinite(point.v)) {
      if (current.length > 0) runs.push(current);
      current = [];
      continue;
    }

    if (current.length > 0 && point.t - current[current.length - 1].t > maxGap) {
      runs.push(current);
      current = [];
    }

    current.push({ t: point.t, v: point.v });
  }

  if (current.length > 0) runs.push(current);
  return runs;
};

/**
 * Largest-Triangle-Three-Buckets downsampling.
 *
 * Keeps the visual shape of a long series in a bounded number of points. Good for player counts
 * and popularity, where the silhouette is what matters. Not used for tick timings — see
 * `minMaxBucket`, which cannot drop a spike.
 */
export const lttb = (points: { t: number; v: number }[], threshold: number): { t: number; v: number }[] => {
  if (threshold >= points.length || threshold < 3) return points;

  const sampled: { t: number; v: number }[] = [points[0]];
  const bucketSize = (points.length - 2) / (threshold - 2);

  let previous = 0;

  for (let i = 0; i < threshold - 2; i += 1) {
    // Average of the *next* bucket forms the third vertex of the candidate triangles.
    const nextStart = Math.floor((i + 1) * bucketSize) + 1;
    const nextEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, points.length);

    let avgT = 0;
    let avgV = 0;
    const nextCount = Math.max(1, nextEnd - nextStart);
    for (let j = nextStart; j < nextEnd; j += 1) {
      avgT += points[j].t;
      avgV += points[j].v;
    }
    avgT /= nextCount;
    avgV /= nextCount;

    const rangeStart = Math.floor(i * bucketSize) + 1;
    const rangeEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, points.length);

    const anchor = points[previous];
    let bestArea = -1;
    let bestIndex = rangeStart;

    for (let j = rangeStart; j < rangeEnd; j += 1) {
      const area = Math.abs(
        (anchor.t - avgT) * (points[j].v - anchor.v) -
          (anchor.t - points[j].t) * (avgV - anchor.v),
      );
      if (area > bestArea) {
        bestArea = area;
        bestIndex = j;
      }
    }

    sampled.push(points[bestIndex]);
    previous = bestIndex;
  }

  sampled.push(points[points.length - 1]);
  return sampled;
};

/**
 * Min/max bucket downsampling: two points per bucket, in time order.
 *
 * Unlike LTTB this **cannot** discard an extreme, so a one-sample lag spike survives. That is
 * the whole question for a tick-time chart ("did we hit 200ms?"), so this is the default for
 * MSPT and TPS.
 */
export const minMaxBucket = (
  points: { t: number; v: number }[],
  threshold: number,
): { t: number; v: number }[] => {
  if (threshold >= points.length || threshold < 4) return points;

  const buckets = Math.floor(threshold / 2);
  const bucketSize = points.length / buckets;
  const sampled: { t: number; v: number }[] = [];

  for (let i = 0; i < buckets; i += 1) {
    const start = Math.floor(i * bucketSize);
    const end = Math.min(Math.floor((i + 1) * bucketSize), points.length);
    if (end <= start) continue;

    let minPoint = points[start];
    let maxPoint = points[start];
    for (let j = start + 1; j < end; j += 1) {
      if (points[j].v < minPoint.v) minPoint = points[j];
      if (points[j].v > maxPoint.v) maxPoint = points[j];
    }

    // Emit in time order so the path never doubles back on itself.
    if (minPoint.t <= maxPoint.t) {
      sampled.push(minPoint);
      if (maxPoint !== minPoint) sampled.push(maxPoint);
    } else {
      sampled.push(maxPoint);
      sampled.push(minPoint);
    }
  }

  return sampled;
};

/**
 * Downsample a null-aware series to at most `maxPoints`.
 *
 * Runs are downsampled independently and re-joined, so gaps survive and no interpolation happens
 * across an outage. Each run gets a share of the budget proportional to its length.
 */
export const downsampleRuns = (
  runs: { t: number; v: number }[][],
  maxPoints: number,
  strategy: 'lttb' | 'minmax' | 'none' = 'lttb',
): { t: number; v: number }[][] => {
  if (strategy === 'none') return runs;

  const total = runs.reduce((sum, run) => sum + run.length, 0);
  if (total <= maxPoints) return runs;

  return runs.map((run) => {
    const budget = Math.max(4, Math.floor((run.length / total) * maxPoints));
    if (run.length <= budget) return run;
    return strategy === 'minmax' ? minMaxBucket(run, budget) : lttb(run, budget);
  });
};

/** Index of the sample nearest `t`. Binary search — this runs on every pointer move. */
export const nearestIndex = (points: { t: number }[], t: number): number => {
  if (points.length === 0) return -1;

  let low = 0;
  let high = points.length - 1;

  while (low < high) {
    const mid = (low + high) >> 1;
    if (points[mid].t < t) low = mid + 1;
    else high = mid;
  }

  // `low` is the first sample at or after `t`; its predecessor may be nearer.
  if (low > 0 && Math.abs(points[low - 1].t - t) <= Math.abs(points[low].t - t)) {
    return low - 1;
  }
  return low;
};

/** `M x,y L x,y …` for each run, so gaps become separate subpaths. */
export const buildLinePath = (
  runs: { x: number; y: number }[][],
): string =>
  runs
    .filter((run) => run.length > 0)
    .map((run) =>
      run
        .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(''),
    )
    .join(' ');

/** Closed path per run, dropped to `baselineY`, for the single-series area fill. */
export const buildAreaPath = (
  runs: { x: number; y: number }[][],
  baselineY: number,
): string =>
  runs
    .filter((run) => run.length > 1)
    .map((run) => {
      const line = run
        .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join('');
      const first = run[0];
      const last = run[run.length - 1];
      return `${line}L${last.x.toFixed(1)},${baselineY.toFixed(1)}L${first.x.toFixed(1)},${baselineY.toFixed(1)}Z`;
    })
    .join(' ');
